/**
 * SmartPaywall - Platform-aware paywall component
 * 
 * Shows the Plan sheet (tier selector) on all platforms.
 * - Web/PWA: Stripe checkout (card, Apple Pay, Google Pay)
 * - Despia Native (iOS/Android): RevenueCat IAP via native runtime
 * 
 * On native, the Plan sheet is shown first so users can pick a tier,
 * then tapping "Subscribe" launches the RevenueCat native paywall.
 */

import React, { lazy, Suspense, useEffect, useState, useCallback, useRef } from 'react';
import { isDespiaRuntime } from '@/hooks/use-despia';
import { useAuth } from '@/components/AuthProvider';
import { useQueryClient } from '@tanstack/react-query';
import { launchNativeRevenueCatPaywall, resetRevenueCatLaunchLock } from '@/lib/native-revenuecat-launch';
import { stageNativePaywallHandoff } from '@/lib/native-paywall-handoff';

// Lazy load tier selector
const SubscriptionTierSelector = lazy(() => 
  import('@/components/subscription/SubscriptionTierSelector').then(m => ({ default: m.SubscriptionTierSelector }))
);

interface SmartPaywallProps {
  isOpen: boolean;
  onClose: () => void;
  onPurchaseComplete?: () => void;
  /** RevenueCat offering identifier - defaults to 'default' */
  offering?: string;
  /** Launch RevenueCat paywall without a user ID (anonymous mode for payment-first flows) */
  anonymousMode?: boolean;
}

// Extend Window interface for RevenueCat callbacks
declare global {
  interface Window {
    onRevenueCatPurchase?: () => void;
    onRevenueCatError?: (error: { code: string; message: string }) => void;
    onRevenueCatDismiss?: () => void;
  }
}

export function SmartPaywall({ 
  isOpen, 
  onClose, 
  onPurchaseComplete,
  offering = 'default',
  anonymousMode = false,
}: SmartPaywallProps) {
  const isNative = isDespiaRuntime();
  const platform = usePaymentPlatform();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [nativeProcessing, setNativeProcessing] = useState(false);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pollCountRef = useRef(0);

  // Log platform detection on mount
  useEffect(() => {
    if (isOpen) {
      console.log('[SmartPaywall] === Payment Platform Detection ===');
      console.log('[SmartPaywall] isNative:', isNative);
      console.log('[SmartPaywall] Platform:', platform.platform);
      console.log('[SmartPaywall] Provider:', platform.paymentProvider);
      console.log('[SmartPaywall] Store:', platform.storeName);
      console.log('[SmartPaywall] Offering:', offering);
    }
  }, [isOpen, isNative, platform, offering]);

  // Cleanup polling on unmount/close
  useEffect(() => {
    if (!isOpen) {
      setNativeProcessing(false);
      resetRevenueCatLaunchLock();
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    }
    return () => {
      resetRevenueCatLaunchLock();
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [isOpen]);

  // Poll backend for subscription after RevenueCat purchase
  const pollSubscriptionStatus = useCallback(async () => {
    if (!user?.id) return false;
    try {
      await queryClient.invalidateQueries({ queryKey: ['subscriptionStatus'] });
      const { supabase } = await import('@/integrations/supabase/client');
      const { data } = await supabase
        .from('user_accounts')
        .select('subscription_status, current_period_end')
        .eq('user_id', user.id)
        .maybeSingle();
      
      // Treat status=active as active even without current_period_end
      if (data?.subscription_status === 'active') {
        if (!data.current_period_end || new Date(data.current_period_end) > new Date()) {
          return true;
        }
      }
      return false;
    } catch {
      return false;
    }
  }, [user?.id, queryClient]);

  // Handle native RevenueCat subscribe
  const handleNativeSubscribe = useCallback((tier: 'weekly' | 'monthly' | 'annual') => {
    if (!anonymousMode && !user?.id) return;
    
    setNativeProcessing(true);
    console.log('[SmartPaywall] Native subscribe - tier:', tier, 'offering:', offering);

    // Register callbacks before launching
    window.onRevenueCatPurchase = async () => {
      resetRevenueCatLaunchLock();
      console.log('[SmartPaywall] RevenueCat purchase callback — verifying with backend...');
      
      const { verifyRevenueCatPurchase } = await import('@/lib/verifyRevenueCatPurchase');
      const result = await verifyRevenueCatPurchase(2);
      
      if (result.verified) {
        console.log('[SmartPaywall] ✓ Backend verified! Completing purchase.');
        await queryClient.invalidateQueries({ queryKey: ['subscriptionStatus'] });
        setNativeProcessing(false);
        onPurchaseComplete?.();
        onClose();
      } else {
        console.warn('[SmartPaywall] ✗ Backend did not confirm subscription, falling back to polling...');
        pollCountRef.current = 0;
        pollIntervalRef.current = setInterval(async () => {
          pollCountRef.current++;
          console.log(`[SmartPaywall] Poll attempt ${pollCountRef.current}/15`);
          const confirmed = await pollSubscriptionStatus();
          if (confirmed || pollCountRef.current >= 15) {
            clearInterval(pollIntervalRef.current!);
            pollIntervalRef.current = null;
            setNativeProcessing(false);
            if (confirmed) {
              onPurchaseComplete?.();
              onClose();
            }
          }
        }, 2000);
      }
    };

    window.onRevenueCatError = (error) => {
      resetRevenueCatLaunchLock();
      console.error('[SmartPaywall] RevenueCat error:', error);
      setNativeProcessing(false);
    };

    window.onRevenueCatDismiss = () => {
      resetRevenueCatLaunchLock();
      console.log('[SmartPaywall] RevenueCat dismissed');
      setNativeProcessing(false);
    };

    void launchNativeRevenueCatPaywall({
      externalId: anonymousMode ? undefined : user.id,
      offering,
      logPrefix: '[SmartPaywall]',
      onBeforeLaunch: () => stageNativePaywallHandoff('[SmartPaywall]'),
      onLaunchError: (error) => {
        console.error('[SmartPaywall] Failed to launch paywall:', error);
        setNativeProcessing(false);
      },
    });
  }, [user?.id, offering, pollSubscriptionStatus, onPurchaseComplete, onClose]);

  // On native Despia, launch RevenueCat paywall directly (skip tier selector)
  useEffect(() => {
    if (isOpen && isNative && (user?.id || anonymousMode)) {
      console.log('[SmartPaywall] Native detected - launching RevenueCat directly, anonymous:', anonymousMode);
      handleNativeSubscribe('annual');
    }
  }, [isOpen, isNative, user?.id, anonymousMode]);

  if (!isOpen) return null;

  // On native, show a minimal processing overlay (RevenueCat native UI handles the rest)
  if (isNative) {
    if (nativeProcessing) {
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
          <div className="animate-pulse text-muted-foreground">Processing...</div>
        </div>
      );
    }
    return null; // RevenueCat native paywall is showing
  }

  // Web/PWA: show the tier selector with Stripe
  const LoadingFallback = (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
      <div className="animate-pulse text-muted-foreground">Loading...</div>
    </div>
  );

  return (
    <Suspense fallback={LoadingFallback}>
      <SubscriptionTierSelector
        isOpen={isOpen}
        onClose={onClose}
        onSelectTier={async (tier, price) => {
          onPurchaseComplete?.();
        }}
      />
    </Suspense>
  );
}

/**
 * Hook to determine which payment platform to use
 */
export function usePaymentPlatform() {
  const isNative = isDespiaRuntime();
  
  // Import these lazily to avoid issues
  let isIOS = false;
  let isAndroid = false;
  try {
    const { isDespiaIOS, isDespiaAndroid } = require('@/hooks/use-despia');
    isIOS = isDespiaIOS();
    isAndroid = isDespiaAndroid();
  } catch {}

  let platform: 'ios' | 'android' | 'web' = 'web';
  if (isIOS) platform = 'ios';
  else if (isAndroid) platform = 'android';

  return {
    isNative,
    platform,
    paymentProvider: isNative ? 'revenuecat' : 'stripe' as const,
    storeName: isIOS ? 'App Store' : isAndroid ? 'Google Play' : 'Web',
  };
}

export default SmartPaywall;
